/**
 * Market Hours Utility
 * Handles US stock market hours validation (Mon-Fri 9:30-16:00 EST, excluding holidays)
 */

import { StockData } from '../types/stock';
import { logger } from '../utils/logger';

// Symbols that trade 24 hours and should bypass regular market hour filtering
const TWENTY_FOUR_HOUR_SYMBOLS = new Set<string>([
  'GC=F'
]);

export function isSymbol24HourMarket(symbol?: string | null): boolean {
  if (!symbol) {
    return false;
  }
  return TWENTY_FOUR_HOUR_SYMBOLS.has(symbol.toUpperCase());
}

// US Stock Market Holidays for 2024-2025
const MARKET_HOLIDAYS = [
  // 2024
  '2024-01-01', // New Year's Day
  '2024-01-15', // Martin Luther King Jr. Day
  '2024-02-19', // Presidents' Day
  '2024-03-29', // Good Friday
  '2024-05-27', // Memorial Day
  '2024-06-19', // Juneteenth
  '2024-07-04', // Independence Day
  '2024-09-02', // Labor Day
  '2024-11-28', // Thanksgiving Day
  '2024-12-25', // Christmas Day

  // 2025
  '2025-01-01', // New Year's Day
  '2025-01-20', // Martin Luther King Jr. Day
  '2025-02-17', // Presidents' Day
  '2025-04-18', // Good Friday
  '2025-05-26', // Memorial Day
  '2025-06-19', // Juneteenth
  '2025-07-04', // Independence Day
  '2025-09-01', // Labor Day
  '2025-11-27', // Thanksgiving Day
  '2025-12-25', // Christmas Day
];

/**
 * Check if a given date is a market holiday
 */
export function isMarketHoliday(date: Date): boolean {
  const dateStr = date.toISOString().split('T')[0]; // YYYY-MM-DD format
  return MARKET_HOLIDAYS.includes(dateStr);
}

/**
 * Check if a given date/time is within market hours
 * Market hours: Monday-Friday 9:30 AM - 4:00 PM EST
 */
export function isWithinMarketHours(timestamp: number): boolean {
  const date = new Date(timestamp * 1000); // Convert to milliseconds

  // Check if it's a weekend (Saturday = 6, Sunday = 0)
  const dayOfWeek = date.getDay();
  if (dayOfWeek === 0 || dayOfWeek === 6) {
    return false;
  }

  // Check if it's a market holiday
  if (isMarketHoliday(date)) {
    return false;
  }

  // Convert to EST/EDT timezone using Intl.DateTimeFormat for accurate conversion
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });

  const parts = formatter.formatToParts(date);
  const hours = parseInt(parts.find(part => part.type === 'hour')?.value || '0');
  const minutes = parseInt(parts.find(part => part.type === 'minute')?.value || '0');
  const timeInMinutes = hours * 60 + minutes;

  // Market hours: 9:30 AM (570 minutes) to 4:00 PM (960 minutes)
  const marketOpenMinutes = 9 * 60 + 30; // 9:30 AM
  const marketCloseMinutes = 16 * 60; // 4:00 PM

  const isWithinHours = timeInMinutes >= marketOpenMinutes && timeInMinutes <= marketCloseMinutes;

  return isWithinHours;
}

/**
 * Filter stock data to only include market hours data
 */
export function filterMarketHoursData(data: StockData[], interval?: string, symbol?: string): StockData[] {
  if (!interval || interval.toLowerCase() === '1d') {
    return data;
  }

  if (isSymbol24HourMarket(symbol)) {
    return data;
  }

  const filtered = data.filter(item => isWithinMarketHours(item.time));
  logger.log(`🔍 Market hours filtering: ${filtered.length} out of ${data.length} data points are within market hours`);

  return filtered;
}

/**
 * Get the next market open time for a given timestamp
 */
export function getNextMarketOpen(timestamp: number): number {
  const date = new Date(timestamp * 1000);
  const estDate = new Date(date.toLocaleString("en-US", {timeZone: "America/New_York"}));

  // Set to 9:30 AM EST
  estDate.setHours(9, 30, 0, 0);

  // If it's already past 9:30 AM today, move to next business day
  if (timestamp * 1000 >= estDate.getTime()) {
    estDate.setDate(estDate.getDate() + 1);
  }

  // Find next business day (skip weekends and holidays)
  while (estDate.getDay() === 0 || estDate.getDay() === 6 || isMarketHoliday(estDate)) {
    estDate.setDate(estDate.getDate() + 1);
  }

  return Math.floor(estDate.getTime() / 1000);
}

/**
 * Get the previous market close time for a given timestamp
 */
export function getPreviousMarketClose(timestamp: number): number {
  const date = new Date(timestamp * 1000);
  const estDate = new Date(date.toLocaleString("en-US", {timeZone: "America/New_York"}));

  // Set to 4:00 PM EST
  estDate.setHours(16, 0, 0, 0);

  // If it's before 4:00 PM today, move to previous business day
  if (timestamp * 1000 < estDate.getTime()) {
    estDate.setDate(estDate.getDate() - 1);
  }

  // Find previous business day (skip weekends and holidays)
  while (estDate.getDay() === 0 || estDate.getDay() === 6 || isMarketHoliday(estDate)) {
    estDate.setDate(estDate.getDate() - 1);
  }

  return Math.floor(estDate.getTime() / 1000);
}

/**
 * Check if the current time is during market hours
 */
export function isCurrentlyMarketHours(): boolean {
  return isWithinMarketHours(Math.floor(Date.now() / 1000));
}

/**
 * Get market status information
 */
export function getMarketStatus(): {
  isOpen: boolean;
  nextOpen?: number;
  lastClose?: number;
  message: string;
} {
  const now = Math.floor(Date.now() / 1000);
  const isOpen = isWithinMarketHours(now);

  if (isOpen) {
    return {
      isOpen: true,
      message: 'Market is currently open'
    };
  } else {
    const nextOpen = getNextMarketOpen(now);
    const lastClose = getPreviousMarketClose(now);

    return {
      isOpen: false,
      nextOpen,
      lastClose,
      message: 'Market is currently closed'
    };
  }
}

/**
 * Format market hours for display
 */
export function formatMarketHours(): string {
  return 'Mon-Fri 9:30 AM - 4:00 PM EST (excluding holidays)';
}
